/**
 * CMS-11 "Dispositivos" (docs/06 §2.10): the three platform counters, the filtered table and the
 * drawer — first/last seen, hosted sessions, last user, the block toggle, notes and the pending
 * grants `/tv/pair/precheck` will announce for this device (docs/07 §3).
 *
 * Block and notes are admin-only (`PATCH /cms/devices/:id`) and audited.
 */
import type {
  CmsDeviceDetailResponse,
  CmsDevicesQueryInput,
  CmsDevicesResponse,
  DeviceDTO,
  Platform,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Drawer } from '../components/ui/drawer';
import { Field, Input, Select, Textarea } from '../components/ui/field';
import { Badge, Panel } from '../components/ui/panel';
import { Switch } from '../components/ui/switch';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatDateTime, formatNumber } from '../lib/format';

export interface DeviceFilters {
  q: string;
  platform: Platform | '';
  blocked: 'yes' | 'no' | '';
  seenDays: '' | '7' | '30' | '90';
}

export const EMPTY_DEVICE_FILTERS: DeviceFilters = {
  q: '',
  platform: '',
  blocked: '',
  seenDays: '',
};

const SORT_IDS = new Set(['id', 'platform', 'model', 'first_seen', 'last_seen', 'sessions']);

export function toDevicesQuery(
  filters: DeviceFilters,
  page: number,
  sorting: SortingState,
): CmsDevicesQueryInput {
  const query: CmsDevicesQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.platform) query.platform = filters.platform;
  if (filters.blocked) query.blocked = filters.blocked;
  if (filters.seenDays) query.seenDays = Number(filters.seenDays);
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

function Counter({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Panel className="px-4 py-3">
      <p className="text-[13px] font-semibold text-ink">{label}</p>
      <p className="mt-1 text-[26px] leading-8 font-bold text-ink tabular-nums">{value}</p>
      {hint ? <Badge className="mt-1.5">{hint}</Badge> : null}
    </Panel>
  );
}

function DeviceDrawerBody({
  device,
  canEdit,
  onClose,
}: {
  device: DeviceDTO;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const [blocked, setBlocked] = useState(device.blocked);
  const [notes, setNotes] = useState(device.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery<CmsDeviceDetailResponse>({
    queryKey: ['cms', 'device', device.id],
    queryFn: () => api.cmsDevice({ params: { id: device.id } }),
  });

  const confirm = useConfirm();

  const save = useMutation({
    mutationFn: (body: { blocked?: boolean; notes?: string | null }) =>
      api.cmsDevicePatch({ params: { id: device.id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'devices'] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'device', device.id] });
    },
    onError: () => setError(t('common:states.error')),
  });

  /** Blocking a device stops it pairing, so it asks first; unblocking does not (UX-211). */
  const onToggleBlock = async (next: boolean) => {
    if (next) {
      const ok = await confirm({
        title: t('cms:confirm.blockDevice.title'),
        body: t('cms:confirm.blockDevice.body'),
        confirmLabel: t('cms:confirm.blockDevice.confirm'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBlocked(next);
    save.mutate({ blocked: next });
  };

  const pending = detail.data?.pendingGrants ?? [];
  const lastUser = detail.data?.lastUser ?? null;

  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-[13px]">
        <dt className="text-[12px] font-semibold tracking-wide text-muted uppercase">
          {t('cms:devices.drawer.firstSeen')}
        </dt>
        <dd className="text-ink">{formatDateTime(device.firstSeenAt, locale)}</dd>
        <dt className="text-[12px] font-semibold tracking-wide text-muted uppercase">
          {t('cms:devices.drawer.lastSeen')}
        </dt>
        <dd className="text-ink">{formatDateTime(device.lastSeenAt, locale)}</dd>
        <dt className="text-[12px] font-semibold tracking-wide text-muted uppercase">
          {t('cms:devices.drawer.hostedSessions')}
        </dt>
        <dd className="text-ink tabular-nums">{formatNumber(device.sessionCount, locale)}</dd>
        <dt className="text-[12px] font-semibold tracking-wide text-muted uppercase">
          {t('cms:devices.drawer.lastUser')}
        </dt>
        <dd className="text-ink">
          {lastUser?.email ?? lastUser?.displayName ?? t('cms:devices.drawer.none')}
        </dd>
      </dl>

      <div className="flex items-center justify-between rounded-[10px] border border-line bg-canvas px-3 py-2.5">
        <span>
          {/* A real <label for>: the switch is a native input (UX-209). */}
          <label
            htmlFor={`device-blocked-${device.id}`}
            className="block text-[13px] font-semibold text-ink"
          >
            {t('cms:devices.drawer.blockTitle')}
          </label>
          <span className="block text-[12px] text-muted">{t('cms:devices.drawer.blockHelp')}</span>
        </span>
        <Switch
          id={`device-blocked-${device.id}`}
          checked={blocked}
          disabled={!canEdit || save.isPending}
          onCheckedChange={(next) => void onToggleBlock(next)}
        />
      </div>

      <section className="rounded-[12px] border border-line" data-testid="device-pending-grants">
        <header className="border-b border-line px-3 py-2">
          <h3 className="text-[13px] font-bold text-ink">{t('cms:devices.drawer.pendingTitle')}</h3>
          <p className="text-[12px] text-muted">{t('cms:devices.drawer.pendingHelp')}</p>
        </header>
        {pending.length > 0 ? (
          <ul className="divide-y divide-line">
            {pending.map((grant) => (
              <li key={grant.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span>
                  <span className="block text-[13px] font-semibold text-ink">
                    {t('cms:grants.accessFull')} ·{' '}
                    {grant.durationDays === null
                      ? t('cms:grants.perpetual')
                      : t('cms:grants.days', { count: grant.durationDays })}
                  </span>
                  <span className="block text-[12px] text-muted">
                    {t('cms:devices.drawer.createdBy', {
                      who: grant.createdByEmail ?? grant.createdBy,
                      date: formatDateTime(grant.createdAt, locale),
                    })}
                  </span>
                </span>
                <Badge tone="warn">{t('cms:status.pending')}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-3 py-3 text-[12px] text-muted">{t('cms:devices.drawer.pendingEmpty')}</p>
        )}
      </section>

      <Field label={t('cms:devices.drawer.notes')}>
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            value={notes}
            disabled={!canEdit}
            onChange={(e) => setNotes(e.target.value)}
          />
        )}
      </Field>

      {canEdit && (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => save.mutate({ notes: notes.trim() || null })}
          >
            {t('cms:devices.drawer.save')}
          </Button>
        </div>
      )}

      {error && (
        <p className="text-[12px] text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Devices() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canEdit = atLeast(role, 'admin');

  const [filters, setFilters] = useState<DeviceFilters>(EMPTY_DEVICE_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'last_seen', desc: true }]);
  const [selected, setSelected] = useState<DeviceDTO | null>(null);

  const query = toDevicesQuery(filters, page, sorting);
  const result = useQuery<CmsDevicesResponse>({
    queryKey: ['cms', 'devices', query],
    queryFn: () => api.cmsDevices({ query }),
    placeholderData: keepPreviousData,
  });

  /**
   * No counter until the query answers: `?? { total: 0, … }` made the three tiles read
   * "Dispositivos vistos 0 · Android TV 0 · 0 % del total" for the whole first load before
   * settling on 454 (QA 2026-09-15 UX-530). While they are unknown the tiles show an ellipsis
   * and no share.
   */
  const counts = result.data?.counts ?? null;
  const share = (value: number) =>
    !counts || counts.total === 0 ? '0' : String(Math.round((value / counts.total) * 100));
  const counter = (value: number | undefined) =>
    value === undefined ? '…' : formatNumber(value, locale);

  const update = (patch: Partial<DeviceFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const columns = useMemo<ColumnDef<DeviceDTO>[]>(
    () => [
      {
        id: 'id',
        header: t('cms:devices.columns.id'),
        cell: ({ row }) => (
          <span className="font-mono text-[12px] break-all text-ink">{row.original.id}</span>
        ),
      },
      {
        id: 'platform',
        header: t('cms:devices.columns.platform'),
        cell: ({ row }) => (
          <Badge tone={row.original.platform === 'android_tv' ? 'cyan' : 'neutral'}>
            {row.original.platform}
          </Badge>
        ),
      },
      {
        id: 'model',
        header: t('cms:devices.columns.model'),
        cell: ({ row }) => row.original.model ?? '—',
      },
      {
        id: 'app',
        header: t('cms:devices.columns.app'),
        cell: ({ row }) => row.original.appVersion ?? '—',
      },
      {
        id: 'last_seen',
        header: t('cms:devices.columns.lastSeen'),
        cell: ({ row }) => formatDateTime(row.original.lastSeenAt, locale),
      },
      {
        id: 'sessions',
        header: t('cms:devices.columns.sessions'),
        cell: ({ row }) => (
          <span className="tabular-nums">{formatNumber(row.original.sessionCount, locale)}</span>
        ),
      },
      {
        id: 'pendingGrants',
        header: t('cms:devices.columns.pendingGrants'),
        cell: ({ row }) =>
          row.original.pendingGrants > 0 ? (
            <Badge tone="warn">{row.original.pendingGrants}</Badge>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: 'blocked',
        header: t('cms:devices.filters.status'),
        cell: ({ row }) =>
          row.original.blocked ? (
            <Badge tone="danger">{t('cms:devices.filters.blocked')}</Badge>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<DeviceDTO>[] = [
    { header: 'id', value: (d) => d.id },
    { header: 'platform', value: (d) => d.platform },
    { header: 'model', value: (d) => d.model },
    { header: 'app_version', value: (d) => d.appVersion },
    { header: 'first_seen_at', value: (d) => d.firstSeenAt },
    { header: 'last_seen_at', value: (d) => d.lastSeenAt },
    { header: 'sessions', value: (d) => d.sessionCount },
    { header: 'pending_grants', value: (d) => d.pendingGrants },
    { header: 'blocked', value: (d) => d.blocked },
    { header: 'notes', value: (d) => d.notes },
  ];

  return (
    <>
      <PageHeader title={t('cms:devices.title')} />

      <div className="flex flex-col gap-4 px-6 pb-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Counter label={t('cms:devices.counts.total')} value={counter(counts?.total)} />
          <Counter
            label={t('cms:devices.counts.androidTv')}
            value={counter(counts?.androidTv)}
            hint={
              counts
                ? t('cms:devices.counts.share', { percent: share(counts.androidTv) })
                : undefined
            }
          />
          <Counter
            label={t('cms:devices.counts.web')}
            value={counter(counts?.web)}
            hint={
              counts ? t('cms:devices.counts.share', { percent: share(counts.web) }) : undefined
            }
          />
        </div>

        <DataTable
          columns={columns}
          data={result.data?.items ?? []}
          getRowId={(row) => row.id}
          label={t('cms:devices.title')}
          total={result.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={setSelected}
          /**
           * QA 2026-09-15 **UX-560**: fifty rows on /devices were all named «Abrir «Browser»» —
           * one name for the whole page, because the label was the model alone and every web
           * device reports the same one. The id is what tells two devices apart.
           */
          rowLabel={(row) =>
            t('cms:table.openRow', {
              name: t('cms:devices.rowName', {
                model: row.model ?? row.platform,
                id: row.id,
              }),
            })
          }
          sortableIds={SORT_IDS}
          selectedRowId={selected?.id ?? null}
          emptyLabel={t('cms:devices.empty')}
          csv={{
            filename: 'akapella-devices.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsDevices({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <Select
                className="w-44"
                aria-label={t('cms:devices.filters.platform')}
                value={filters.platform}
                onChange={(e) => update({ platform: e.target.value as Platform | '' })}
              >
                <option value="">{t('cms:devices.filters.allPlatforms')}</option>
                <option value="android_tv">android_tv</option>
                <option value="web">web</option>
              </Select>
              <Select
                className="w-40"
                aria-label={t('cms:devices.filters.status')}
                value={filters.blocked}
                onChange={(e) => update({ blocked: e.target.value as DeviceFilters['blocked'] })}
              >
                <option value="">{t('cms:devices.filters.allStatuses')}</option>
                <option value="no">{t('cms:devices.filters.notBlocked')}</option>
                <option value="yes">{t('cms:devices.filters.blocked')}</option>
              </Select>
              <Select
                className="w-40"
                aria-label={t('cms:devices.filters.lastSeen')}
                value={filters.seenDays}
                onChange={(e) => update({ seenDays: e.target.value as DeviceFilters['seenDays'] })}
              >
                <option value="">{t('cms:devices.filters.anyTime')}</option>
                <option value="7">{t('cms:devices.filters.days', { count: 7 })}</option>
                <option value="30">{t('cms:devices.filters.days', { count: 30 })}</option>
                <option value="90">{t('cms:devices.filters.days', { count: 90 })}</option>
              </Select>
              <Input
                className="w-64"
                placeholder={t('cms:devices.searchPlaceholder')}
                aria-label={t('cms:devices.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <Button variant="link" size="sm" onClick={() => update(EMPTY_DEVICE_FILTERS)}>
                {t('cms:table.clearFilters')}
              </Button>
            </>
          }
        />
      </div>

      {selected && (
        <DeviceDrawer device={selected} canEdit={canEdit} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function DeviceDrawer({
  device,
  canEdit,
  onClose,
}: {
  device: DeviceDTO;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  return (
    <Drawer
      open
      onClose={onClose}
      title={`${t('cms:devices.title')} ${device.id}`}
      subtitle={t('cms:devices.drawer.meta', {
        platform: device.platform,
        model: device.model ?? '—',
        app: device.appVersion ?? '—',
      })}
    >
      <DeviceDrawerBody device={device} canEdit={canEdit} onClose={onClose} />
    </Drawer>
  );
}
